import * as React from 'react';
import { useTranslation } from 'react-i18next';
import {
  LedgerItem,
  remarksBelow,
  remarksOf,
} from '../../ledger/ledgerInstrument';
import { RemarkIndicator, RemarkItem } from '../../RemarkPopover';
import './LabelAlerts.scss';

/** What the alerts column reads off a tree item, beyond its remarks. */
export type LabelAlertsItem = LedgerItem & {
  fileData?: { id?: number };
  folderData?: { id?: number | string };
};

export type LabelAlertsProps = {
  item?: LabelAlertsItem;
  /** The row's full name, for the panel its remark dot opens. */
  name: string;
  /** True while the row is open: a closed row also counts the issues under it. */
  expanded?: boolean;
  /** A document or contract, as opposed to a folder that only groups them. */
  isInstrument: boolean;
  /** Last in the column: a section's expand / collapse toggle. */
  endAdornment?: React.ReactNode;
};

/**
 * The ledger's Alerts column, the last in the row: the row's own remarks as
 * the dot that opens them, on a closed row how many issues sit on the rows
 * folded into it, and after them a section's expand / collapse toggle, which
 * has no column of its own.
 */
export const LabelAlerts: React.FC<LabelAlertsProps> = ({
  item,
  name,
  expanded,
  isInstrument,
  endAdornment,
}) => {
  const { t } = useTranslation();

  const remark = item ? (remarksOf(item) as RemarkItem[]) : [];
  const below = React.useMemo(
    () => (!expanded && item?.children?.length ? remarksBelow(item) : null),
    [expanded, item]
  );

  if (!isInstrument && !remark.length && !below?.count && !endAdornment) {
    return null;
  }

  const isFolder = item?.fileType === 'folder';

  return (
    <span className="tree-label-alerts">
      {remark.length > 0 && (
        <RemarkIndicator
          remark={remark}
          // The record's own id, not the tree node's: a node id is a path
          // built for the tree (`7---sorted/folder-3/file-7`).
          source={{
            id: isFolder ? item?.folderData?.id : item?.fileData?.id,
            name,
            isFolder,
          }}
          label={t('View missing clauses')}
        />
      )}
      {!!below?.count && (
        // Not a control of its own: the click falls through to the row, which
        // opens it to show where the issues are.
        <span
          className={`tree-ledger-badge tree-ledger-badge--roll tree-ledger-badge--${below.severity}`}
          title={t('{{count}} issues on the rows below this one', {
            count: below.count,
          })}
        >
          {below.count}
        </span>
      )}
      {!remark.length && !below?.count && !endAdornment && (
        <span className="tree-ledger-none">—</span>
      )}
      {endAdornment && (
        <span className="tree-label-alerts__end">{endAdornment}</span>
      )}
    </span>
  );
};
